import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DocumentGallery } from '../components/DocumentGallery';
import { DocumentLoadStatus } from '../components/DocumentLoadStatus';
import { ErrorBox, Loading } from '../components/Feedback';
import { ReplacementEditor } from '../components/ReplacementEditor';
import { ServerTimer } from '../components/ServerTimer';
import { TicketHeader } from '../components/TicketHeader';
import { api, ticketRunId } from '../lib/api';
import { prepareImageFiles } from '../lib/image-file';
import { sendDocumentBatch } from '../lib/document-upload';
import { useTicketDocuments } from '../lib/use-ticket-documents';
import { mergeTicketPayload } from '../lib/ticket-payload';
import { useTickets } from '../lib/ticket-state';
import { can, workPhase } from '../lib/workflow';
import type { PartReplacementLine, StockItem, Ticket } from '../types';

function replacementsKey(id: string, runId: string) { return `nova:replacements:${id}:${runId}`; }

export function ProgressPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const routeRunId = params.get('runId') || '';
  const navigate = useNavigate();
  const { upsert } = useTickets();
  const [ticket, setTicket] = useState<Ticket>();
  const { documents, documentsReady, documentsLoading, documentsError, reloadDocuments, acceptUpload } = useTicketDocuments(id, ticketRunId(ticket || {}, routeRunId), Boolean(ticket));
  const [uploadMessage, setUploadMessage] = useState('');
  const [stock, setStock] = useState<StockItem[]>([]);
  const [catalog, setCatalog] = useState<StockItem[]>([]);
  const [replacements, setReplacements] = useState<PartReplacementLine[]>(() => {
    try { return JSON.parse(localStorage.getItem(replacementsKey(id, routeRunId)) || '[]'); }
    catch { return []; }
  });
  const [showParts, setShowParts] = useState(() => replacements.length > 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [detail, state] = await Promise.all([
        api.ticket(id, routeRunId),
        api.intervention(id, routeRunId),
      ]);
      const next = mergeTicketPayload(detail, state, routeRunId);
      setTicket(next);
      upsert(next);
      const [stockRows, catalogRows] = await Promise.all([
        api.stock().catch(() => []),
        api.partCatalog().catch(() => []),
      ]);
      setStock(stockRows);
      setCatalog(catalogRows);
      setError('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Intervenția nu poate fi încărcată.');
    }
  }, [id, routeRunId, upsert]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const resume = () => { if (document.visibilityState === 'visible') { void refresh(); if (!busy) void reloadDocuments(); } };
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('nova:ticket-change', resume);
    return () => {
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('nova:ticket-change', resume);
    };
  }, [refresh, reloadDocuments, busy]);
  useEffect(() => {
    localStorage.setItem(replacementsKey(id, routeRunId), JSON.stringify(replacements));
  }, [id, routeRunId, replacements]);

  async function toggle() {
    if (!ticket) return;
    const action = ticket.intervention?.state === 'paused' ? 'resume' : 'pause';
    // Recheck on click as well as disabling the button when the API denies it.
    if (busy || !can(ticket, action)) return;
    setBusy(true); setError('');
    try {
      const result = await api.action(id, action, ticket);
      const next = mergeTicketPayload(ticket, result, routeRunId);
      setTicket(next); upsert(next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Starea intervenției nu a putut fi schimbată.');
    } finally { setBusy(false); }
  }

  async function upload(event: ChangeEvent<HTMLInputElement>, imageUpload = false) {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selectedFiles.length || busy) return;
    setBusy(true); setError(''); setUploadMessage('');
    try {
      const files = imageUpload ? await prepareImageFiles(selectedFiles) : selectedFiles;
      const result = await sendDocumentBatch(files, (file) => api.upload(id, [file], ticketRunId(ticket || {}, routeRunId)), acceptUpload);
      setUploadMessage(result.message);
      setError(result.error);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Fișierele nu au putut fi încărcate.');
    } finally { setBusy(false); }
  }

  if (!ticket) return error ? <ErrorBox message={error} /> : <Loading />;
  const active = ticket.intervention?.state === 'active';
  const paused = ticket.intervention?.state === 'paused';
  const phase = workPhase(ticket);
  const requiredPhotos = Number(ticket.intervention?.requiredPhotos ?? ticket.requiredPhotos ?? 0);
  const photoCount = documents.filter((item) => String(item.mimeType || '').startsWith('image/') || !item.mimeType).length;
  const selectedCount = replacements.filter((line) => line.installed || line.removed).length;

  return <div className="page">
    <TicketHeader ticket={ticket} title={paused ? 'Intervenție oprită' : 'În lucru'} activeTimer={active} />
    <section className="page-body with-cta">
      <article className="card"><h2>{ticket.deviceName || ticket.clientName || ticket.locationName}</h2><p>{ticket.clientName} · {ticket.locationName}</p></article>
      <article className="card"><span className="eyebrow">Problema raportată</span><p>{ticket.description || '—'}</p></article>
      {!active && <div className={`timer-card ${paused ? 'paused' : ''}`}><span>{paused ? 'Intervenție în pauză' : 'Timp de lucru'}</span><ServerTimer ticket={ticket} /></div>}
      <Link className="btn btn-secondary block equipment-history-button" to={`/tickets/${encodeURIComponent(id)}/history${ticket.runId || routeRunId ? `?runId=${encodeURIComponent(ticket.runId || routeRunId)}` : ''}`}>Istoric aparat</Link>
      <section>
        <div className="section-title"><h2>Documente și fotografii ({documentsReady ? documents.length : '—'})</h2><div className="photo-actions"><label className="btn btn-secondary">Foto<input disabled={busy} className="native-file-input" type="file" accept="image/*" capture="environment" aria-label="Fotografie tichet din cameră" onChange={(event) => void upload(event, true)} /></label><label className="btn btn-secondary">Galerie<input disabled={busy} className="native-file-input" type="file" accept="image/*" multiple aria-label="Fotografii tichet din galerie" onChange={(event) => void upload(event, true)} /></label><label className="btn btn-secondary">Document<input disabled={busy} className="native-file-input" type="file" multiple aria-label="Documente tichet" onChange={(event) => void upload(event)} /></label></div></div>
        {uploadMessage && <div className="alert alert-info" role="status">{uploadMessage}</div>}
        <DocumentLoadStatus loading={documentsLoading} error={documentsError} retryAvailable={Boolean(uploadMessage)} disabled={busy} onRetry={() => void reloadDocuments()} />
        {documentsReady && requiredPhotos > photoCount && <div className="alert">Mai sunt necesare {requiredPhotos - photoCount} fotografii înainte de finalizare.</div>}
        <DocumentGallery documents={documents} />
      </section>
      {(phase === 'field' || phase === 'service') && <section className="parts-picker">
        <button type="button" className="btn btn-secondary block parts-picker__toggle" onClick={() => setShowParts((value) => !value)}>{showParts ? 'Închide selecția pieselor' : `+ Adaugă piese${selectedCount ? ` (${selectedCount})` : ''}`}</button>
        {showParts && <><div className="section-title"><h2>Piese montate și demontate ({selectedCount})</h2></div><ReplacementEditor stock={stock} catalog={catalog} value={replacements} onChange={setReplacements} /></>}
      </section>}
      <button className="btn btn-secondary block" disabled={busy || !(paused ? can(ticket, 'resume') : can(ticket, 'pause'))} onClick={() => void toggle()}>{paused ? 'Reia intervenția' : 'Oprește temporar intervenția'}</button>
      {error && <ErrorBox message={error} />}
    </section>
    <div className="cta"><button className="btn btn-primary block" disabled={busy || (requiredPhotos > 0 && (!documentsReady || requiredPhotos > photoCount))} onClick={() => { const runId = ticketRunId(ticket, routeRunId); navigate(`/tickets/${encodeURIComponent(id)}/close${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`); }}>Închide intervenția →</button></div>
  </div>;
}
